<template>
  <!-- ============ ROUTE PAGE MODE: Modern full-page chatbot ============ -->
  <div v-if="isRoutePage" class="route-chatbot">
    <!-- Minimal top bar -->
    <div class="route-topbar">
      <button @click="goBack" class="route-back-btn">
        <i class="fas fa-arrow-left"></i>
        <span>{{ $t('chatbot.title') }}</span>
      </button>
      <button v-if="messages.length > 1" @click="clearChat" class="route-clear-btn">
        <i class="fas fa-trash-alt"></i>
      </button>
    </div>

    <!-- Welcome state: centered vertically -->
    <div v-if="messages.length <= 1" class="route-welcome-wrap">
      <div class="route-welcome-content">
        <!-- Icon + greeting -->
        <div class="route-greeting">
          <div class="route-bot-icon">
            <i class="fas fa-robot"></i>
          </div>
          <h1 class="route-title">{{ $t('chatbot.welcome') }}</h1>
        </div>

        <!-- Input field (primary focus) -->
        <div class="route-input-wrap">
          <div class="route-input-box">
            <input
              ref="inputField"
              v-model="inputText"
              @keyup.enter="sendMessage"
              type="text"
              :placeholder="$t('chatbot.placeholder')"
              class="route-input"
              :disabled="isLoading"
            />
            <button
              @click="sendMessage"
              :disabled="!inputText.trim() || isLoading"
              class="route-send-btn"
            >
              <i :class="isLoading ? 'fas fa-circle-notch fa-spin' : 'fas fa-paper-plane'"></i>
            </button>
          </div>
        </div>

        <!-- Capabilities -->
        <div class="route-capabilities">
          <div v-for="(item, index) in $tm('chatbot.welcomeItems')" :key="index" class="route-cap-item">
            <i class="fas fa-check-circle route-cap-icon"></i>
            <span>{{ item }}</span>
          </div>
        </div>

        <!-- Example questions as cards -->
        <div class="route-examples">
          <p class="route-examples-label">{{ $t('chatbot.exampleQuestions') }}</p>
          <div class="route-examples-grid">
            <button
              v-for="(example, index) in $tm('chatbot.examples')"
              :key="index"
              @click="sendExample(example)"
              class="route-example-card"
            >
              <i class="fas fa-arrow-right"></i>
              <span>{{ example }}</span>
            </button>
          </div>
        </div>
      </div>
      <p class="route-powered">{{ $t('chatbot.poweredBy') }}</p>
    </div>

    <!-- Conversation state -->
    <div v-else class="route-conversation">
      <!-- Messages area -->
      <div ref="messagesContainer" class="route-messages">
        <div class="route-messages-inner">
          <div
            v-for="message in messages"
            :key="message.id"
            class="route-msg"
            :class="message.type === 'user' ? 'route-msg-user' : 'route-msg-bot'"
          >
            <div v-if="message.type === 'bot'" class="route-msg-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="route-msg-body" :class="[message.type === 'user' ? 'route-msg-body-user' : 'route-msg-body-bot', message.isError ? 'route-msg-error' : '']">
              <div class="msg-content" v-html="formatMessage(message.text)"></div>
              <div v-if="message.suggestions && message.suggestions.length" class="route-msg-suggestions">
                <button
                  v-for="(s, idx) in message.suggestions"
                  :key="idx"
                  @click="sendExample(s)"
                  class="route-suggestion-chip"
                >{{ s }}</button>
              </div>
              <span class="route-msg-time">{{ formatTime(message.timestamp) }}</span>
            </div>
            <div v-if="message.type === 'user'" class="route-msg-avatar route-msg-avatar-user">
              <i class="fas fa-user"></i>
            </div>
          </div>

          <!-- Typing indicator -->
          <div v-if="isLoading" class="route-msg route-msg-bot">
            <div class="route-msg-avatar">
              <i class="fas fa-robot"></i>
            </div>
            <div class="route-msg-body-bot route-typing">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input pinned at bottom -->
      <div class="route-conv-footer">
        <div class="route-conv-input-wrap">
          <div class="route-input-box">
            <input
              ref="inputFieldConv"
              v-model="inputText"
              @keyup.enter="sendMessage"
              type="text"
              :placeholder="$t('chatbot.placeholder')"
              class="route-input"
              :disabled="isLoading"
            />
            <button
              @click="sendMessage"
              :disabled="!inputText.trim() || isLoading"
              class="route-send-btn"
            >
              <i :class="isLoading ? 'fas fa-circle-notch fa-spin' : 'fas fa-paper-plane'"></i>
            </button>
          </div>
          <p class="route-powered-sm">{{ $t('chatbot.poweredBy') }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- ============ WIDGET MODE: Original floating chatbot ============ -->
  <div v-else class="fixed bottom-6 right-6 z-50" :class="{ 'chatbot-fullscreen': isFullscreen }">
    <transition name="bounce">
      <button
        v-if="!isOpen"
        @click="openChat"
        class="chat-fab group"
      >
        <div class="relative">
          <i class="fas fa-comments text-2xl"></i>
          <span v-if="hasNewMessage" class="notif-dot"></span>
        </div>
        <span class="chat-tooltip">{{ $t('chatbot.openChat') }}</span>
      </button>
    </transition>

    <transition name="slide-up">
      <div
        v-if="isOpen && !isMinimized"
        ref="chatWindow"
        class="chat-window"
        :class="{ 'fullscreen-window': isFullscreen }"
        :style="!isFullscreen ? windowStyle : {}"
      >
        <div
          v-if="!isFullscreen"
          class="resize-handle"
          @mousedown.prevent="startResize"
          @touchstart.prevent="startResizeTouch"
        >
          <div class="resize-grip"></div>
        </div>

        <div class="chat-header">
          <div class="flex items-center gap-3">
            <div class="header-avatar">
              <i class="fas fa-robot text-lg"></i>
            </div>
            <div class="leading-tight">
              <h3 class="font-semibold text-white text-sm tracking-wide">{{ $t('chatbot.title') }}</h3>
              <p class="text-[11px] text-white/60">{{ $t('chatbot.subtitle') }}</p>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <button @click="toggleFullscreen" class="header-btn" :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'">
              <i :class="isFullscreen ? 'fas fa-compress-alt' : 'fas fa-expand-alt'" class="text-xs"></i>
            </button>
            <button @click="minimizeChat" class="header-btn">
              <i class="fas fa-minus text-xs"></i>
            </button>
            <button @click="closeChat" class="header-btn header-btn-close">
              <i class="fas fa-times text-xs"></i>
            </button>
          </div>
        </div>

        <div ref="messagesContainer" class="chat-messages">
          <div v-if="messages.length <= 1" class="welcome-section">
            <div class="welcome-icon-wrap">
              <div class="welcome-icon">
                <i class="fas fa-robot text-2xl text-emerald-600"></i>
              </div>
              <div class="welcome-pulse"></div>
            </div>
            <p class="text-gray-500 text-sm text-center mb-5 max-w-[260px] mx-auto leading-relaxed">
              {{ $t('chatbot.welcome') }}
            </p>

            <ul class="space-y-2 mb-6">
              <li v-for="(item, index) in $tm('chatbot.welcomeItems')" :key="index" class="welcome-item">
                <i class="fas fa-check-circle text-emerald-500 text-xs mt-0.5 shrink-0"></i>
                <span>{{ item }}</span>
              </li>
            </ul>

            <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">{{ $t('chatbot.exampleQuestions') }}</p>
            <div class="space-y-1.5">
              <button
                v-for="(example, index) in $tm('chatbot.examples')"
                :key="index"
                @click="sendExample(example)"
                class="example-btn"
              >
                <i class="fas fa-arrow-right text-emerald-500 text-[10px] shrink-0 mt-0.5"></i>
                <span>{{ example }}</span>
              </button>
            </div>
          </div>

          <div v-else class="space-y-3">
            <div
              v-for="message in messages"
              :key="message.id"
              class="msg-row"
              :class="message.type === 'user' ? 'justify-end' : 'justify-start'"
            >
              <div v-if="message.type === 'bot'" class="msg-avatar bot-avatar">
                <i class="fas fa-robot text-[11px]"></i>
              </div>

              <div
                class="msg-bubble"
                :class="[
                  message.type === 'user' ? 'user-msg' : 'bot-msg',
                  message.isError ? 'error-msg' : ''
                ]"
              >
                <div class="msg-content" v-html="formatMessage(message.text)"></div>

                <div v-if="message.suggestions && message.suggestions.length" class="msg-suggestions">
                  <button
                    v-for="(s, idx) in message.suggestions"
                    :key="idx"
                    @click="sendExample(s)"
                    class="suggestion-chip"
                  >{{ s }}</button>
                </div>

                <span class="msg-time">{{ formatTime(message.timestamp) }}</span>
              </div>

              <div v-if="message.type === 'user'" class="msg-avatar user-avatar">
                <i class="fas fa-user text-[11px]"></i>
              </div>
            </div>

            <div v-if="isLoading" class="msg-row justify-start">
              <div class="msg-avatar bot-avatar">
                <i class="fas fa-robot text-[11px]"></i>
              </div>
              <div class="bot-msg typing-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-footer">
          <div class="input-row">
            <button v-if="messages.length > 1" @click="clearChat" class="clear-btn" :title="$t('chatbot.clearChat')">
              <i class="fas fa-trash-alt text-xs"></i>
            </button>
            <input
              ref="inputField"
              v-model="inputText"
              @keyup.enter="sendMessage"
              type="text"
              :placeholder="$t('chatbot.placeholder')"
              class="chat-input"
              :disabled="isLoading"
            />
            <button
              @click="sendMessage"
              :disabled="!inputText.trim() || isLoading"
              class="send-btn"
            >
              <i :class="isLoading ? 'fas fa-circle-notch fa-spin' : 'fas fa-paper-plane'" class="text-sm"></i>
            </button>
          </div>
          <p class="powered-by">{{ $t('chatbot.poweredBy') }}</p>
        </div>
      </div>
    </transition>

    <transition name="bounce">
      <button v-if="isOpen && isMinimized" @click="restoreChat" class="chat-fab minimized-fab">
        <div class="relative">
          <i class="fas fa-comments text-xl"></i>
          <span v-if="hasNewMessage" class="notif-dot"></span>
        </div>
      </button>
    </transition>
  </div>
</template>

<script>
import { useChatbotStore } from '@/stores/chatbot';
import { mapState, mapActions } from 'pinia';

export default {
  name: 'ChatBot',

  data() {
    return {
      inputText: '',
      isFullscreen: false,
      windowWidth: 380,
      windowHeight: 520,
      isResizing: false,
      startX: 0,
      startY: 0,
      startW: 0,
      startH: 0,
    };
  },

  computed: {
    ...mapState(useChatbotStore, ['messages', 'isOpen', 'isMinimized', 'isLoading', 'hasNewMessage']),

    windowStyle() {
      return {
        width: this.windowWidth + 'px',
        height: this.windowHeight + 'px',
      };
    },

    isRoutePage() {
      return this.$route.name === 'chatbot';
    },
  },

  methods: {
    ...mapActions(useChatbotStore, ['toggleChat', 'openChat', 'closeChat', 'minimizeChat', 'restoreChat', 'clearMessages', 'initializeChat']),

    async sendMessage() {
      if (!this.inputText.trim() || this.isLoading) return;
      const text = this.inputText.trim();
      this.inputText = '';
      const store = useChatbotStore();
      await store.sendMessage(text, this.$i18n.locale);
      this.$nextTick(() => {
        this.scrollToBottom();
        if (this.isRoutePage && this.$refs.inputFieldConv) {
          this.$refs.inputFieldConv.focus();
        }
      });
    },

    async sendExample(text) {
      this.inputText = text;
      await this.sendMessage();
    },

    clearChat() {
      this.clearMessages();
      this.initializeChat(this.$i18n.locale);
    },

    toggleFullscreen() {
      const lang = this.$i18n.locale || 'en';
      this.closeChat();
      this.$router.push({ name: 'chatbot', params: { lang } });
    },

    goBack() {
      this.$router.back();
    },

    startResize(e) {
      this.isResizing = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startW = this.windowWidth;
      this.startH = this.windowHeight;
      document.addEventListener('mousemove', this.onResize);
      document.addEventListener('mouseup', this.stopResize);
      document.body.style.userSelect = 'none';
    },

    startResizeTouch(e) {
      const touch = e.touches[0];
      this.isResizing = true;
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.startW = this.windowWidth;
      this.startH = this.windowHeight;
      document.addEventListener('touchmove', this.onResizeTouch, { passive: false });
      document.addEventListener('touchend', this.stopResize);
    },

    onResize(e) {
      if (!this.isResizing) return;
      const dx = this.startX - e.clientX;
      const dy = this.startY - e.clientY;
      this.windowWidth = Math.min(700, Math.max(320, this.startW + dx));
      this.windowHeight = Math.min(window.innerHeight - 80, Math.max(400, this.startH + dy));
    },

    onResizeTouch(e) {
      if (!this.isResizing) return;
      e.preventDefault();
      const touch = e.touches[0];
      const dx = this.startX - touch.clientX;
      const dy = this.startY - touch.clientY;
      this.windowWidth = Math.min(700, Math.max(320, this.startW + dx));
      this.windowHeight = Math.min(window.innerHeight - 80, Math.max(400, this.startH + dy));
    },

    stopResize() {
      this.isResizing = false;
      document.removeEventListener('mousemove', this.onResize);
      document.removeEventListener('mouseup', this.stopResize);
      document.removeEventListener('touchmove', this.onResizeTouch);
      document.removeEventListener('touchend', this.stopResize);
      document.body.style.userSelect = '';
    },

    scrollToBottom() {
      const c = this.$refs.messagesContainer;
      if (c) c.scrollTop = c.scrollHeight;
    },

    formatTime(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp).toLocaleTimeString(this.$i18n.locale, { hour: '2-digit', minute: '2-digit' });
    },

    formatMessage(text) {
      if (!text) return '';
      let s = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      s = s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      s = s.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
      return s;
    }
  },

  watch: {
    isOpen(val) {
      if (val) {
        this.initializeChat(this.$i18n.locale);
        this.$nextTick(() => { this.scrollToBottom(); this.$refs.inputField?.focus(); });
      }
    },
    messages: {
      handler() { this.$nextTick(() => this.scrollToBottom()); },
      deep: true
    },
    '$i18n.locale'(lang) {
      if (this.messages.length <= 1) { this.clearMessages(); this.initializeChat(lang); }
    }
  },

  mounted() {
    if (this.$route.name === 'chatbot') {
      this.openChat();
      this.initializeChat(this.$i18n.locale);
      this.$nextTick(() => {
        this.$refs.inputField?.focus();
      });
    } else if (this.isOpen) {
      this.initializeChat(this.$i18n.locale);
    }
  },

  beforeUnmount() {
    this.stopResize();
  }
};
</script>

<style scoped>


*:not(.fas):not(.far):not(.fab):not(.fa):not(.fa-solid):not(.fa-regular):not(.fa-brands) {
  font-family: 'DM Sans', sans-serif;
}

/* ================================================================
   ROUTE PAGE MODE
   ================================================================ */

.route-chatbot {
  width: 100%;
  min-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
  background: #f9fafb;
}

.route-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.route-back-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  border: none;
  background: none;
  color: #374151;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}
.route-back-btn:hover {
  background: #f3f4f6;
  color: #111827;
}
.route-back-btn i {
  font-size: 12px;
  color: #6b7280;
}

.route-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s;
}
.route-clear-btn:hover {
  background: #fef2f2;
  color: #ef4444;
  border-color: #fecaca;
}

/* --- Welcome state --- */
.route-welcome-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 24px 16px;
  overflow-y: auto;
}

.route-welcome-content {
  width: 100%;
  max-width: 680px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.route-greeting {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 28px;
}

.route-bot-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 22px;
  margin-bottom: 16px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.25);
}

.route-title {
  font-size: 18px;
  font-weight: 500;
  color: #4b5563;
  text-align: center;
  line-height: 1.5;
  max-width: 400px;
}

/* --- Primary input --- */
.route-input-wrap {
  width: 100%;
  max-width: 600px;
  margin-bottom: 28px;
}

.route-input-box {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #d1d5db;
  border-radius: 14px;
  padding: 4px 4px 4px 18px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  transition: all 0.25s;
}
.route-input-box:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.route-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  color: #1f2937;
  background: transparent;
  padding: 10px 8px 10px 0;
  min-width: 0;
}
.route-input::placeholder {
  color: #9ca3af;
}
.route-input:disabled {
  opacity: 0.5;
}

.route-send-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  font-size: 14px;
}
.route-send-btn:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 2px 10px rgba(37, 99, 235, 0.3);
}
.route-send-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* --- Capabilities --- */
.route-capabilities {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 16px;
  margin-bottom: 28px;
}

.route-cap-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
}
.route-cap-icon {
  color: #3b82f6;
  font-size: 11px;
}

/* --- Examples --- */
.route-examples {
  width: 100%;
  max-width: 600px;
}

.route-examples-label {
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 10px;
  text-align: center;
}

.route-examples-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.route-example-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  color: #374151;
  line-height: 1.45;
  transition: all 0.2s;
}
.route-example-card:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.08);
}
.route-example-card i {
  color: #3b82f6;
  font-size: 10px;
  margin-top: 3px;
  flex-shrink: 0;
}

.route-powered {
  font-size: 11px;
  color: #d1d5db;
  text-align: center;
  margin-top: 20px;
  letter-spacing: 0.3px;
}

/* --- Conversation state --- */
.route-conversation {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.route-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px 16px;
  scroll-behavior: smooth;
}
.route-messages::-webkit-scrollbar { width: 5px; }
.route-messages::-webkit-scrollbar-track { background: transparent; }
.route-messages::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

.route-messages-inner {
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.route-msg {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.route-msg-user {
  justify-content: flex-end;
}
.route-msg-bot {
  justify-content: flex-start;
}

.route-msg-avatar {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
  font-size: 13px;
}
.route-msg-avatar-user {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: white;
}

.route-msg-body {
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.6;
  min-width: 0;
}

.route-msg-body-bot {
  background: white;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.route-msg-body-user {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  border-bottom-right-radius: 4px;
}

.route-msg-error {
  background: #fef2f2 !important;
  color: #991b1b !important;
  border-color: #fecaca !important;
}

.route-msg-time {
  display: block;
  font-size: 10px;
  margin-top: 6px;
  opacity: 0.45;
}

.route-msg-suggestions {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.route-suggestion-chip {
  padding: 5px 12px;
  font-size: 12px;
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}
.route-suggestion-chip:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

.route-typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 14px 18px !important;
}
.route-typing span {
  width: 8px;
  height: 8px;
  background: #94a3b8;
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out both;
}
.route-typing span:nth-child(1) { animation-delay: 0s; }
.route-typing span:nth-child(2) { animation-delay: 0.16s; }
.route-typing span:nth-child(3) { animation-delay: 0.32s; }

.route-conv-footer {
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 16px 24px 12px;
  flex-shrink: 0;
}

.route-conv-input-wrap {
  max-width: 720px;
  margin: 0 auto;
}

.route-powered-sm {
  font-size: 10px;
  color: #d1d5db;
  text-align: center;
  margin-top: 8px;
}

@media (max-width: 640px) {
  .route-examples-grid {
    grid-template-columns: 1fr;
  }
  .route-capabilities {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .route-topbar {
    padding: 10px 16px;
  }
  .route-welcome-wrap {
    padding: 16px;
  }
  .route-msg-body {
    max-width: 85%;
  }
}

/* ================================================================
   WIDGET MODE — Original floating chatbot (unchanged from production)
   ================================================================ */

.chat-fab {
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.4);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
}
.chat-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 28px rgba(37, 99, 235, 0.5);
}

.minimized-fab {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.4);
}

.notif-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 10px;
  height: 10px;
  background: #ef4444;
  border-radius: 50%;
  border: 2px solid white;
  animation: pulse-dot 2s infinite;
}

.chat-tooltip {
  position: absolute;
  right: calc(100% + 12px);
  padding: 6px 12px;
  background: #1e293b;
  color: white;
  font-size: 12px;
  border-radius: 8px;
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.chat-fab:hover .chat-tooltip { opacity: 1; }

.chat-window {
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.04),
    0 8px 40px rgba(0,0,0,0.12),
    0 2px 8px rgba(0,0,0,0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 320px;
  min-height: 400px;
  max-height: calc(100vh - 80px);
  transition: box-shadow 0.3s;
}

.fullscreen-window {
  position: fixed !important;
  top: auto !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: calc(100vh - 140px) !important;
  max-height: calc(100vh - 140px) !important;
  border-radius: 0 !important;
  z-index: 999;
}

.chatbot-fullscreen {
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100% !important;
  height: auto !important;
}

@media (max-width: 640px) {
  .chat-window {
    position: fixed !important;
    bottom: 0 !important;
    right: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: calc(100vh - 20px) !important;
    border-radius: 16px 16px 0 0 !important;
  }
}

.resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 44px;
  height: 44px;
  cursor: nw-resize;
  z-index: 10;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 6px;
}
.resize-grip {
  width: 16px;
  height: 16px;
  position: relative;
  opacity: 0;
  transition: opacity 0.2s;
}
.resize-handle:hover .resize-grip { opacity: 1; }
.resize-grip::before,
.resize-grip::after {
  content: '';
  position: absolute;
  background: rgba(255,255,255,0.5);
  border-radius: 1px;
}
.resize-grip::before {
  width: 12px; height: 2px;
  top: 4px; left: 0;
  transform: rotate(-45deg);
  transform-origin: left center;
}
.resize-grip::after {
  width: 8px; height: 2px;
  top: 9px; left: 0;
  transform: rotate(-45deg);
  transform-origin: left center;
}

.chat-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 50%, #1d4ed8 100%);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  position: relative;
}
.chat-header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
}

.header-avatar {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.header-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: none;
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.header-btn:hover {
  background: rgba(255,255,255,0.18);
  color: white;
}
.header-btn-close:hover {
  background: rgba(239, 68, 68, 0.3);
  color: #fca5a5;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  background: #f8fafb;
  scroll-behavior: smooth;
}
.chat-messages::-webkit-scrollbar { width: 4px; }
.chat-messages::-webkit-scrollbar-track { background: transparent; }
.chat-messages::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

.welcome-section { padding: 8px 4px; }

.welcome-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  position: relative;
}
.welcome-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.welcome-icon i { color: #2563eb; }
.welcome-pulse {
  position: absolute;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #3b82f6;
  opacity: 0;
  animation: welcome-ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
}

.welcome-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}
.welcome-item i { color: #3b82f6; }

.example-btn {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #374151;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;
  line-height: 1.4;
}
.example-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
  transform: translateX(2px);
}
.example-btn i { color: #3b82f6; }

.msg-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.msg-row.justify-end { justify-content: flex-end; }
.msg-row.justify-start { justify-content: flex-start; }

.msg-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.bot-avatar {
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  color: #1e40af;
}
.user-avatar {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: white;
}

.msg-bubble {
  max-width: 78%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 13.5px;
  line-height: 1.55;
  position: relative;
  min-width: 0;
}

.msg-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  min-width: 0;
}
.msg-content :deep(a) {
  color: inherit;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  word-break: break-all;
}
.msg-content :deep(strong) {
  font-weight: 600;
}
.user-msg .msg-content :deep(a) {
  color: #bfdbfe;
}

.user-msg {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  border-bottom-right-radius: 4px;
  box-shadow: 0 1px 4px rgba(37, 99, 235, 0.2);
}

.bot-msg {
  background: white;
  color: #1f2937;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
  border: 1px solid #f0f0f0;
}

.error-msg {
  background: #fef2f2 !important;
  color: #991b1b !important;
  border-color: #fecaca !important;
}

.msg-time {
  display: block;
  font-size: 10px;
  margin-top: 4px;
  opacity: 0.5;
}

.msg-suggestions {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.suggestion-chip {
  padding: 4px 10px;
  font-size: 11px;
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}
.suggestion-chip:hover {
  background: #dbeafe;
  border-color: #93c5fd;
}

.typing-dots {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 16px !important;
}
.typing-dots span {
  width: 7px;
  height: 7px;
  background: #94a3b8;
  border-radius: 50%;
  animation: typing-bounce 1.4s infinite ease-in-out both;
}
.typing-dots span:nth-child(1) { animation-delay: 0s; }
.typing-dots span:nth-child(2) { animation-delay: 0.16s; }
.typing-dots span:nth-child(3) { animation-delay: 0.32s; }

.chat-footer {
  padding: 12px 14px 10px;
  background: white;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-btn {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #f9fafb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.clear-btn:hover {
  background: #fef2f2;
  color: #ef4444;
}

.chat-input {
  flex: 1;
  padding: 9px 16px;
  background: #f3f4f6;
  border: 1.5px solid transparent;
  border-radius: 24px;
  font-size: 13.5px;
  color: #1f2937;
  outline: none;
  transition: all 0.2s;
  min-width: 0;
}
.chat-input::placeholder { color: #9ca3af; }
.chat-input:focus {
  background: white;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.chat-input:disabled { opacity: 0.6; }

.send-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s;
  flex-shrink: 0;
}
.send-btn:hover:not(:disabled) {
  transform: scale(1.06);
  box-shadow: 0 2px 12px rgba(37, 99, 235, 0.35);
}
.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.powered-by {
  font-size: 10px;
  color: #d1d5db;
  text-align: center;
  margin-top: 6px;
  letter-spacing: 0.3px;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.96);
}

.bounce-enter-active { animation: pop-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
.bounce-leave-active { animation: pop-in 0.2s reverse; }

@keyframes pop-in {
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes typing-bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

@keyframes welcome-ping {
  0% { transform: scale(1); opacity: 0.25; }
  75%, 100% { transform: scale(1.4); opacity: 0; }
}
</style>